/**
 * This plugin module is made for every third parties that need
 * to be use the Vue3 Application API [1]
 *
 * @link https://vuejs.org/api/application.html [1]
 */
import { ELEVATION, ICON_THEME, SIZE, SPINNER_TYPE, STATUS, VARIANT } from '~/utils/lib/layout/ui'
import VuePdfEmbed from 'vue-pdf-embed'
// @ts-ignore: false negative import
import drag from 'v-drag'
// @ts-ignore: false negative import
import { facade } from 'vue-input-facade'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import { clone, noop, pathOr, pipeN, singleton, typeOf } from '~/utils/lib/core/prelude'

export default defineNuxtPlugin((nuxtApp) => {
  /**
   * Global properties
   */
  nuxtApp.vueApp.config.globalProperties.UI = {
    SIZE: clone(SIZE),
    STATUS: clone(STATUS),
    ICON_THEME: clone(ICON_THEME),
    VARIANT: clone(VARIANT),
    ELEVATION: clone(ELEVATION),
    SPINNER_TYPE: clone(SPINNER_TYPE),
  }
  /**
  * Focus on Component mount hook
  */
  nuxtApp.vueApp.directive('focus', {
    mounted: (
      /** @type {HTMLElement} */ el,
      /** @type {DirectiveBinding} */ binding
    ) => {
      const isEnabled
        =  binding.value === undefined
        || binding.value === true

      switch (isEnabled) {

        case true:
          if ('focus' in el)
            el.focus(
              {
                preventScroll: true,
                // focusVisible: true
              }
            )
          break

        case false:
          if ('blur' in el)
            el.blur()
          break
      }
    }
  })
  /**
   * @desc Custom directive to use a `v-hide` as truthy to change the element
   *    style with `visibility="hidden"`
   */
  nuxtApp.vueApp.directive('hide', {
    beforeMount: (
      /** @type {HTMLElement} */ el,
      /** @type {DirectiveBinding} */ binding
    ) => {
      const isHidden = binding.value

      el.style.visibility = isHidden ? 'hidden' : 'visible'
    },
    updated: (
      /** @type {HTMLElement} */ el,
      /** @type {DirectiveBinding} */ binding
    ) => {
      const isHidden = binding.value

      el.style.visibility = isHidden ? 'hidden' : 'visible'
    },
  })
  /**
   * @link https://github.com/hrynko/vue-pdf-embed
   */
  nuxtApp.vueApp.component('VuePdfEmbed', VuePdfEmbed)
  /**
   * @link https://github.com/nil/v-drag
   */
  nuxtApp.vueApp.use(drag)
  /**
   * @link https://www.ag-grid.com/vue-data-grid/installation/
   */
  ModuleRegistry.registerModules([AllCommunityModule])
  /**
   * Making a similar directive as the "4D input filter"
   *
   *    [x] works on a `HTMLInputElement`
   *    [ ] works on a `HTMLElement` containing a `HTMLInputElement` (@NOTE:
   *        next milestone?)
   *
   *    [x] prevent duplicated `@input` event
   *
   *    [x] arg: single function
   *    [x] arg: list of functions
   *
   *    [ ] arg changes are listened (@NOTE: next milestone?)
   *
   *    [x] synchronous computation
   *    [ ] asynchronous computation
   *
   *    [x] function arg: current input value
   *    [ ] function arg: previous input value
   *    [ ] function arg: component instance
   *
   *    [x] immediate options (warning: will trigger a `@input` event)
   *
   *    [x] "v-mask" has precedence on "v-filter"
   *
   *
   * @example
   *    <input
   *      v-model="foo"
   *      v-filter="fn"
   *    >
   *
   *    <input
   *      v-model="bar"
   *      v-filter.immediate="[ fn1, fn2 ]"
   *    >
   */
  nuxtApp.vueApp.directive('filter', {
    mounted: (el, binding) => {

      // @ts-ignore: UNSAFE coerce
      const /** @type {HTMLInputElement} */ input = el

      const thunkList = (() => {
        switch (typeOf(binding.value)) {
          case 'array':     return binding.value
          case 'function':  return singleton(binding.value)
          default:          return singleton(noop)
        }
      })()

      const emitSimulatedEvent = () => {
        const simulatedEvent = new CustomEvent('input', {
          bubbles: true,
          cancelable: true,
          detail: {
            simulated: true
          }
        })

        el.dispatchEvent(simulatedEvent)
      }

      const immediateExec = pathOr(false, [ 'modifiers', 'immediate' ], binding)

      if (immediateExec) {
        // @ts-ignore: "ramda" typing limitation
        input.value = pipeN(thunkList)(input.value)
        emitSimulatedEvent()
      }

      input.addEventListener(
        'input',
        (/** @type {Event} */ event) => {

          const vFilterEvent = pathOr(false, [ 'detail', 'simulated' ], event)
          const vMaskEvent = pathOr(false, [ 'detail', 'facade'], event)

          if (vFilterEvent)
            return

          if (!vMaskEvent)
            event.stopPropagation()
          // @ts-ignore: "ramda" typing limitation
          input.value = pipeN(thunkList)(input.value)

          emitSimulatedEvent()
        },
        true
      )
    },
  })
  /**
   * @link https://stackoverflow.com/a/64698630/6003907
   */
  nuxtApp.vueApp.directive('click-outside', {
    beforeMount: (el, binding) => {
      el.clickOutsideEvent = (/** @type {Event} */ event) => {
        // here I check that click was outside the el and his children
        if (!(el == event.target || el.contains(event.target))) {
          // and if it did, call method provided in attribute value
          binding.value()
        }
      };
      document.addEventListener('click', el.clickOutsideEvent)
    },
    unmounted: el => {
      document.removeEventListener('click', el.clickOutsideEvent)
    },
  })
  /**
   * @link https://www.npmjs.com/package/vue-input-facade/v/3.0.0-beta.4
   *
   * This can be used as an equivalent solution as the "4D input mask"
   * (it will however contains less complexity, ie. provided special chars)
   */
  nuxtApp.vueApp.directive('mask', facade)

  /**
   * Reset global fetch state on page navigation
   *
   * @desc
   *  This hook resets the global fetch state when navigating
   *  between pages to prevent sticky loading states.
   *
   *  Can be monitored from the browser's JavaScript thread using
   *  `window.useNuxtApp().state.$sGlobalFetchState`.
   *  This is particularly useful for Playwright E2E testing to wait
   *  for loading states.
   */
  /** @type {Ref<import("#app").AsyncDataRequestStatus|undefined> } */
  const globalFetchStateStatus = useState('globalFetchStateStatus', undefined)

  /// Resetting value on every Nuxt page load
  /// @link https://nuxt.com/docs/4.x/api/advanced/hooks
  nuxtApp.hook('page:loading:start', () => {
    globalFetchStateStatus.value = undefined
  })
})


